import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { Check, ArrowRight, ArrowLeft, Plus, Minus, Users, Building, Shield, Zap, BarChart3, Globe, Instagram, Facebook, Twitter, AlertTriangle } from 'lucide-react';
import { GlassCard } from '@/components/ui/glass-card';
import { formatMoney, TIER_PRICING, calculateTierCost, ANNUAL_FREE_MONTHS } from '@/lib/pricing-utils';
import { supabase } from '@/integrations/supabase/client';

// Display prices come from the shared source of truth
const TIER_KEY = { starter: 'Starter', growth: 'Growth', pro: 'Pro' } as const;
const DISPLAY_PRICE = {
  starter: TIER_PRICING.Starter.unitPrice,
  growth: TIER_PRICING.Growth.unitPrice,
  pro: TIER_PRICING.Pro.unitPrice,
};

const Pricing = () => {
  const [units, setUnits] = useState(1);
  const [tier, setTier] = useState<"starter" | "growth" | "pro">('growth');
  const [interval, setInterval] = useState<"month" | "year">('month');
  const [isEnterpriseModalOpen, setIsEnterpriseModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleUnitsChange = (increment: number) => {
    const newUnits = Math.max(1, Math.min(200, units + increment));
    setUnits(newUnits);
  };

  const startCheckout = async (
    checkoutTier?: "starter" | "growth" | "pro",
    checkoutUnits?: number,
    checkoutInterval?: "month" | "year"
  ) => {
    // Use passed parameters or fall back to state values
    const selectedTier = checkoutTier || tier;
    const selectedUnits = checkoutUnits || units;
    const selectedInterval = checkoutInterval || interval;

    if (selectedUnits < 1 || !selectedTier) {
      toast({
        title: "Invalid configuration",
        description: "Please select a valid tier and unit count"
      });
      return;
    }

    // Require authentication before starting checkout
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session) {
      toast({
        title: "Sign in required",
        description: "Please sign in to complete checkout.",
        variant: "destructive"
      });
      window.location.href = `/auth?redirect=/pricing`;
      return;
    }

    setIsLoading(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('checkout', {
        body: { tier: selectedTier, units: selectedUnits, interval: selectedInterval }
      });
      
      if (error) {
        console.error('Checkout invoke error:', error);
        toast({
          title: "Checkout Error",
          description: error.message || "Failed to start checkout",
          variant: "destructive"
        });
        return;
      }
      
      if (data?.error) {
        console.error('Checkout response error:', data);
        toast({
          title: "Checkout Error",
          description: data.details || data.error,
          variant: "destructive"
        });
        return;
      }
      
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      toast({
        title: "Checkout Error", 
        description: "Failed to start checkout. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  const handleEnterpriseSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: POST to /api/enterprise-lead
    toast({
      title: "Enterprise inquiry submitted",
      description: "Our team will contact you within 24 hours"
    });
    setIsEnterpriseModalOpen(false);
  };
  const scrollToTiers = () => {
    document.getElementById('pricing-tiers')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  // Calculate pricing from the shared source of truth
  const cost = calculateTierCost(TIER_KEY[tier], units, interval === 'year');
  const total = cost.finalTotal;

  const getBreakdown = () => {
    if (interval === 'month') {
      return `${units} ${units === 1 ? 'unit' : 'units'} × ${formatMoney(cost.unitPrice, 'USD', 2)}/mo`;
    }
    return `${units} ${units === 1 ? 'unit' : 'units'} × ${formatMoney(cost.unitPriceAnnual, 'USD', 2)}/yr (${formatMoney(cost.monthlyPerUnit, 'USD', 2)}/mo per unit)`;
  };
  
  const tiers = [
    {
      name: 'starter' as const,
      displayName: 'Starter',
      unitPrice: DISPLAY_PRICE.starter,
      description: 'For independents & small operators',
      features: [
        'Digital HACCP logs with corrective actions',
        'One-click audit reports (PDF/Excel) for inspectors',
        'Automatic law updates for your region',
        '7-day free trial',
        'Email support (72h response)'
      ],
      icon: Users,
      popular: false
    },
    {
      name: 'growth' as const,
      displayName: 'Growth',
      unitPrice: DISPLAY_PRICE.growth,
      description: 'For growing restaurant groups with multiple units',
      features: [
        'Group-wide compliance dashboard (all units in one view)',
        'Role-based staff access (limit who can edit vs. review)',
        'Advanced reporting & analytics to spot weak points',
        'Live regulation sync per location/region',
        'Priority email support (48h response)'
      ],
      icon: BarChart3,
      popular: true
    },
    {
      name: 'pro' as const,
      displayName: 'Pro',
      unitPrice: DISPLAY_PRICE.pro,
      description: 'For enterprises with complex compliance needs',
      features: [
        'Regional compliance presets (auto-apply local rules)',
        'Customizable checklists & workflows for unique operations',
        'Full audit history & export filters (prove compliance anytime)',
        'Standard email support (48–72h response)'
      ],
      icon: Zap,
      popular: false
    }
  ];
  return <div className="min-h-screen bg-background">
      {/* Back Button */}
      <div className="container mx-auto px-6 pt-6">
        <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="h-4 w-4" />
          Back to home
        </Link>
      </div>

      {/* Hero Section */}
      <section className="relative px-6 pt-12 pb-16 text-center">
        <div className="mx-auto max-w-4xl">
          <h1 className="heading-hero mb-6 font-bold my-0 py-[15px]">
            HACCP Compliance Software for Restaurants & Food Businesses
          </h1>
          <p className="subheading mb-12">
            Stay inspection-ready with region-specific food safety rules, digital HACCP checklists, and instant audit reports — all in one platform.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="cta-primary" size="lg">
              Start Your 7-Day Free Trial
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button variant="secondary" size="lg">
              See Full Compliance Features
            </Button>
          </div>
        </div>
      </section>

      {/* ROI Calculator Section */}
      <section className="px-6 pb-16">
        <div className="mx-auto max-w-6xl">
          <GlassCard className="p-8 lg:p-12">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                What it replaces
              </h2>
              <p className="text-white/70 text-lg">
                The same day's records, kept on paper versus kept in Crystal HACCP
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Cost Without Crystal HACCP */}
              <div className="glass-panel p-6">
                <h3 className="text-xl font-semibold text-red-400 mb-6 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  On paper
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center pb-3 border-b border-white/10">
                    <span className="text-white/80">Temps written down, then retyped later</span>
                    <span className="text-white font-semibold">Twice</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-white/10">
                    <span className="text-white/80">Out-of-range reading</span>
                    <span className="text-white font-semibold">Noticed at review, if at all</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-white/10">
                    <span className="text-white/80">Corrective action</span>
                    <span className="text-white font-semibold">Written from memory</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-white/10">
                    <span className="text-white/80">Missing entries</span>
                    <span className="text-white font-semibold">Chased by hand</span>
                  </div>
                  <div className="flex justify-between items-center pt-4">
                    <span className="text-white font-bold">Audit pack</span>
                    <span className="text-red-400 font-bold text-xl">Assembled from binders</span>
                  </div>
                </div>
              </div>

              {/* With Crystal HACCP */}
              <div className="glass-panel p-6 border-2 border-green-500/30">
                <h3 className="text-xl font-semibold text-green-400 mb-6 flex items-center gap-2">
                  <Check className="h-5 w-5" />
                  With Crystal HACCP
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center pb-3 border-b border-white/10">
                    <span className="text-white/80">Temps logged on the phone, once</span>
                    <span className="text-green-400 font-semibold">Under a minute</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-white/10">
                    <span className="text-white/80">Out-of-range reading</span>
                    <span className="text-green-400 font-semibold">Flagged as you enter it</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-white/10">
                    <span className="text-white/80">Corrective action</span>
                    <span className="text-green-400 font-semibold">Attached to the deviation</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-white/10">
                    <span className="text-white/80">Missing entries</span>
                    <span className="text-green-400 font-semibold">Visible on the dashboard</span>
                  </div>
                  <div className="flex justify-between items-center pt-4">
                    <span className="text-white font-bold">Audit pack</span>
                    <span className="text-green-400 font-semibold">One-tap export</span>
                  </div>
                  <div className="flex justify-between items-center pt-2">
                    <span className="text-white font-bold">Monthly cost</span>
                    <span className="text-green-400 font-bold text-xl">from {formatMoney(DISPLAY_PRICE.starter, 'USD', 2)}/unit</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Break-Even Callout */}
            <div className="mt-8 text-center glass-panel-elevated p-6">
              <div className="text-3xl font-bold text-white mb-2">
                Costs less than the hours it replaces
              </div>
              <p className="text-white/70">
                Per unit, per month, cancel any time. No setup fee and no training day.
              </p>
            </div>
          </GlassCard>
        </div>
      </section>

      {/* Unit Selector */}
      <section className="px-6 pb-16">
        <div className="mx-auto max-w-2xl">
          <GlassCard className="p-8 text-center">
            <h3 className="text-2xl font-semibold text-white mb-6">Build Your Compliance Package</h3>
            <p className="text-white/80 text-sm mb-6">
              Select the number of units you want covered. Pricing updates automatically so you can see your compliance costs up front — no surprises.
            </p>
            
            {/* Billing Toggle */}
            <div className="flex items-center justify-center gap-4 mb-6">
              <span className={`text-sm ${interval === 'month' ? 'text-white' : 'text-white/60'}`}>Monthly</span>
              <Switch 
                checked={interval === 'year'} 
                onCheckedChange={(checked) => setInterval(checked ? 'year' : 'month')}
                aria-label="Toggle annual billing" 
              />
              <span className={`text-sm ${interval === 'year' ? 'text-white' : 'text-white/60'}`}>
                Annual <Badge variant="secondary" className="ml-1">{ANNUAL_FREE_MONTHS} months free</Badge>
              </span>
            </div>

            {/* Unit Counter */}
            <div className="flex items-center justify-center gap-4 mb-6">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => handleUnitsChange(-1)} 
                disabled={units <= 1} 
                className="glass-button h-12 w-12"
              >
                <Minus className="h-4 w-4" />
              </Button>
              
              <div className="flex items-center gap-3">
                <Input 
                  type="number" 
                  min="1" 
                  max="200" 
                  value={units} 
                  onChange={e => setUnits(Math.max(1, Math.min(200, parseInt(e.target.value) || 1)))} 
                  className="glass-input w-20 text-center text-lg font-semibold" 
                />
                <span className="text-white/80">units</span>
              </div>
              
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => handleUnitsChange(1)} 
                disabled={units >= 200} 
                className="glass-button h-12 w-12"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {/* Tier Selector */}
            <div className="grid grid-cols-3 gap-2 mb-6">
              {tiers.map(tierOption => (
                <Button
                  key={tierOption.name}
                  variant={tier === tierOption.name ? "default" : "outline"}
                  onClick={() => setTier(tierOption.name)}
                  className={tier === tierOption.name ? "cta-primary" : "glass-button"}
                  size="sm"
                  aria-label={`Select ${tierOption.displayName} tier`}
                >
                  {tierOption.displayName}
                </Button>
              ))}
            </div>

            {/* Pricing Display */}
            <div className="mb-6">
              <div className="text-3xl font-bold text-white mb-2">
                {formatMoney(total, 'USD', 2)}
                <span className="text-lg text-white/60">/{interval}</span>
              </div>
              <div className="text-white/80 text-sm">
                {getBreakdown()}
              </div>
              {interval === 'year' && (
                <div className="text-green-400 text-sm font-semibold mt-2">
                  You save {formatMoney(cost.annualSavings, 'USD', 2)}/year vs monthly billing
                </div>
              )}
            </div>

            <Button 
              variant="cta-primary" 
              onClick={() => startCheckout()}
              disabled={isLoading || units < 1}
              className="w-full sm:w-auto"
            >
              {isLoading ? 'Processing...' : 'Purchase'}
            </Button>
          </GlassCard>
        </div>
      </section>

      {/* Pricing Tiers */}
      <section id="pricing-tiers" className="px-6 pb-20">
        <div className="mx-auto max-w-7xl">
          <h2 className="heading-section text-center mb-16">Choose your plan</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {tiers.map(tierOption => {
              const Icon = tierOption.icon;
              const isSelected = tier === tierOption.name;
              
              return (
                <GlassCard 
                  key={tierOption.name} 
                  className={`relative p-8 cursor-pointer transition-all ${
                    tierOption.popular ? 'ring-2 ring-primary/50' : ''
                  } ${isSelected ? 'ring-2 ring-primary' : ''}`}
                  onClick={() => setTier(tierOption.name)}
                >
                  {tierOption.popular && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 cta-primary">
                      Most Popular
                    </Badge>
                  )}
                  
                  <div className="text-center mb-6">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-white/10 mb-4">
                      <Icon className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">{tierOption.displayName}</h3>
                    
                    <div className="mb-4">
                      <div className="text-3xl font-bold text-white">
                        {formatMoney(tierOption.unitPrice, 'USD', 2)}
                      </div>
                      <div className="text-white/60 text-sm">per unit/month</div>
                      <div className="text-white/50 text-xs mt-1">
                        or {formatMoney(calculateTierCost(TIER_KEY[tierOption.name], 1, true).unitPriceAnnual, 'USD', 2)}/unit/year
                        {' '}— save {formatMoney(calculateTierCost(TIER_KEY[tierOption.name], 1, true).annualSavings, 'USD', 2)}
                      </div>
                    </div>
                    
                    <p className="text-white/80 text-sm">{tierOption.description}</p>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {tierOption.features.map(feature => (
                      <li key={feature} className="flex items-start gap-3">
                        <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span className="text-white/90 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button 
                    onClick={(e) => {
                      e.stopPropagation();
                      startCheckout(tierOption.name, units, interval);
                    }}
                    className={`w-full ${isSelected ? 'cta-primary' : 'cta-secondary'}`}
                    disabled={isLoading}
                  >
                    {tierOption.name === 'starter' ? 'Get Starter Compliance' : 
                     tierOption.name === 'growth' ? 'Secure Growth Compliance' : 
                     'Upgrade to Pro Compliance'}
                  </Button>
                </GlassCard>
              );
            })}
          </div>

          {/* Base Pricing Notice */}
          <div className="text-center mt-8">
            <p className="text-white/60 text-sm">
              Simple per-unit pricing: Starter {formatMoney(DISPLAY_PRICE.starter, 'USD', 2)}, Growth {formatMoney(DISPLAY_PRICE.growth, 'USD', 2)}, Pro {formatMoney(DISPLAY_PRICE.pro, 'USD', 2)} per unit/month.
              Annual plans get {ANNUAL_FREE_MONTHS} months free — you pay for 10, get 12.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="glass-nav border-t border-white/10 px-6 py-8">
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <p className="text-white/90 font-medium mb-1">
                Contact: <a href="mailto:info@crystalhaccp.com" className="text-primary hover:text-primary/80">info@crystalhaccp.com</a>
              </p>
              <p className="text-white/60 text-sm">A Project by The Mikanao Hub</p>
            </div>
            
            <div className="flex items-center gap-4">
              <span className="text-white/80 text-sm">Follow us:</span>
              <div className="flex gap-3">
                <a href="https://instagram.com/mikanao_1519" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-primary transition-colors">
                  <Instagram className="h-5 w-5" />
                </a>
                <a href="https://facebook.com/mikanao1519" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-primary transition-colors">
                  <Facebook className="h-5 w-5" />
                </a>
                <a href="https://x.com/Bigboysnoop" target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-primary transition-colors">
                  <Twitter className="h-5 w-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>;
};
export default Pricing;